import { useState } from 'react';
import { ClientProfileContentItem } from 'store/clientProfile/clientProfileSlice';
import { useAppSelector } from 'store/hooks';

import { NoteForm } from '../NoteForm';
import { useNoteFormContext } from '../context';
import { AutosaveProvider } from '../context/autosaveContext';
import { useNoteFormActions } from '../hooks';

type PopupNoteFormProps = {
  clientId: string;
  note?: ClientProfileContentItem;
  disabled?: boolean;
};

const PopupNoteFormContent = ({
  onEdit,
  onSubmitSuccess
}: {
  onEdit?: () => void;
  onSubmitSuccess?: () => void;
}) => {
  const { isManagerReview, disabled } = useNoteFormContext();
  const { handleSubmit } = useNoteFormActions({ isPopup: true, onSubmitSuccess });
  const isBillingEnabled = useAppSelector((state) => state.clients.client.data?.enableBilling);

  return (
    <NoteForm.Form onSubmit={handleSubmit}>
      <NoteForm.FieldsContainer expanded>
        <NoteForm.FieldsContainer.Row>
          <NoteForm.FieldsContainer.HalfRow>
            <NoteForm.Fields.NoteType disabled={disabled} />
          </NoteForm.FieldsContainer.HalfRow>
          <NoteForm.FieldsContainer.HalfRow>
            <NoteForm.Fields.DateOfEncounter disabled={disabled} />
          </NoteForm.FieldsContainer.HalfRow>
        </NoteForm.FieldsContainer.Row>

        {!isManagerReview && (
          <NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.HalfRow>
              <NoteForm.Fields.StartTime disabled={disabled} />
            </NoteForm.FieldsContainer.HalfRow>
            <NoteForm.FieldsContainer.HalfRow>
              <NoteForm.Fields.TimeSpent disabled={disabled} />
            </NoteForm.FieldsContainer.HalfRow>
          </NoteForm.FieldsContainer.Row>
        )}

        {isManagerReview && (
          <>
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.TimeSpent disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.Manager disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.Chw disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.Provider
                  disabled={disabled}
                  required={Boolean(isBillingEnabled)}
                />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
          </>
        )}

        {!isManagerReview && (
          <>
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.NextContact disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.WhoDidYouReach disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.FullWidth>
              <NoteForm.Fields.TypeOfSupport disabled={disabled} />
            </NoteForm.FieldsContainer.FullWidth>
          </>
        )}
      </NoteForm.FieldsContainer>

      <NoteForm.AiScribe.Status />
      <NoteForm.AiScribe.AudioPlayer />

      <NoteForm.FieldsContainer.HalfRow>
        <NoteForm.Fields.Note disabled={disabled} />
      </NoteForm.FieldsContainer.HalfRow>
      <NoteForm.FieldsContainer.HalfRow>
        <NoteForm.Fields.DocumentationTime disabled={disabled} />
      </NoteForm.FieldsContainer.HalfRow>

      <NoteForm.Actions>
        <NoteForm.Actions.Left>
          <NoteForm.DeleteButton isPopup />
          <NoteForm.EditButton onClick={onEdit} />
        </NoteForm.Actions.Left>
        <NoteForm.Actions.Right>
          <NoteForm.AutosaveIndicator />
          <NoteForm.SubmitButton>Final</NoteForm.SubmitButton>
        </NoteForm.Actions.Right>
      </NoteForm.Actions>
    </NoteForm.Form>
  );
};

/**
 * Preset component for editing notes in a popup.
 * Disabled state is controlled from outside via props.
 * Includes AI Scribe status, transcript, and audio player.
 * Autosave is enabled only for draft notes.
 */
export const PopupNoteForm = ({ clientId, note, disabled = true }: PopupNoteFormProps) => {
  const [isEditing, setIsEditing] = useState(!disabled);
  const onEdit = () => {
    setIsEditing(true);
  };

  return (
    <NoteForm.Root clientId={clientId} note={note} disabled={!isEditing}>
      <AutosaveProvider>
        <PopupNoteFormContent onEdit={onEdit} />
      </AutosaveProvider>
    </NoteForm.Root>
  );
};
